import React, { type FC, useCallback, useState } from 'react';

import { CircleCheckFill, CircleExclamation, CircleInfo, Xmark } from '@gravity-ui/icons';
import { Button, Icon, Label, Link, TextArea, TextInput, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { ApiError } from 'client/api';
import { AliceAppActionButton } from 'client/blocks-redesign/AliceAppActionButton/AliceAppActionButton';
import { GrammarPageFlagsSettings } from 'client/blocks-redesign/grammar/GrammarPageFlagsSettings/GrammarPageFlagsSettings';
import { GrammarPageTestResult } from 'client/blocks-redesign/grammar/GrammarPageTestResult/GrammarPageTestResult';
import { GranetCodeEditor } from 'client/blocks-redesign/grammar/GranetCodeEditor/GranetCodeEditor';
import { Hint } from 'client/blocks-redesign/Hint/Hint';
import { wrapInText } from 'client/lib/i18n-utils';
import { getGranetCompletionProvider } from 'client/lib/monaco/languages/granet/completion';
import { routes } from 'client/lib/routes';
import {
    GrammarIntentEditableFields,
    GrammarIntentShort,
    GrammarIntentTestResult,
    GrammarValidationErrorPayload,
    IntentStatus,
} from 'client/model/grammar';
import * as keyset from 'i18n/IntentsPage';

import styles from './GrammarPageIntentSettings.module.scss';

type FetchingHandlerState = 'loading' | 'error' | 'idle';

interface GrammarPageIntentSettingsFieldProps {
    title: string;
    hint?: React.ReactNode;
    required?: boolean;
    error?: string;
}

interface GrammarPageIntentSettingsProps {
    intent: GrammarIntentShort;
    readOnly?: boolean;
    onClose: () => void;
    customEntitiesNames: string[];
    onTestHandler: () => Promise<void>;
    onSaveHandler: () => Promise<void>;
    onDeleteHandler: () => Promise<void>;
    saveState: FetchingHandlerState;
    testState: FetchingHandlerState;
    deleteState: FetchingHandlerState;
    setValidationError: React.Dispatch<React.SetStateAction<GrammarValidationErrorPayload | null>>;
    validationError: GrammarValidationErrorPayload | null;
    testResults: GrammarIntentTestResult | undefined;
    onChange: <K extends keyof GrammarIntentEditableFields>(key: K) => (value: GrammarIntentEditableFields[K]) => void;
    hasErrors: boolean;
    errors: Partial<Record<keyof GrammarIntentEditableFields, string>>;
    settings: GrammarIntentEditableFields;
}

const GRAMMAR_PAGE_INTENT_TOASTER = 'GRAMMAR_PAGE_INTENT_TOASTER';
const SHOW_TOASTER_DELAY = 20000;

const t = i18n(keyset);

const Status: FC<{ status: IntentStatus }> = ({ status }) => {
    switch (status) {
        case IntentStatus.INVALID_GRAMMAR:
            return (
                <Label
                    className={styles.intentStatus}
                    icon={<Icon data={CircleExclamation} size={12} />}
                    size="s"
                    theme="danger"
                >
                    {t('status-invalid')}
                </Label>
            );
        case IntentStatus.NEW:
            return (
                <Label
                    className={styles.intentStatus}
                    icon={<Icon data={CircleInfo} size={12} />}
                    size="s"
                    theme="info"
                >
                    {t('status-new')}
                </Label>
            );
        case IntentStatus.OK:
            return (
                <Label
                    className={styles.intentStatus}
                    icon={<Icon data={CircleCheckFill} size={12} />}
                    size="s"
                    theme="success"
                >
                    {t('status-published')}
                </Label>
            );
    }
};

const GrammarPageIntentSettingsField: React.FC<GrammarPageIntentSettingsFieldProps> = ({
    children,
    title,
    required,
    hint,
    error,
}) => (
    <div>
        <div>
            <h4
                className={clx(
                    styles.grammarPageIntentSettings__title,
                    required && styles.grammarPageIntentSettings__title_required,
                )}
            >
                {title}
            </h4>
            {!!hint && <Hint hint={hint} />}
        </div>
        {children}
        {!!error && (
            <Label className={styles.grammarPageIntentSettings__label} theme="danger">
                {error}
            </Label>
        )}
    </div>
);

export const GrammarPageIntentSettings: React.FC<GrammarPageIntentSettingsProps> = ({
    readOnly,
    onClose,
    customEntitiesNames,
    onTestHandler,
    onDeleteHandler,
    onSaveHandler,
    saveState,
    testState,
    deleteState,
    setValidationError,
    validationError,
    testResults,
    onChange,
    hasErrors,
    errors,
    settings,
    intent,
}) => {
    const [completionProvider] = useState(() => getGranetCompletionProvider(customEntitiesNames));

    const { add: toasterAdd } = useToaster();

    const isSaving = saveState === 'loading';
    const isTesting = testState === 'loading';
    const isDeleting = deleteState === 'loading';
    const isLoading = isSaving || isTesting || isDeleting;

    const hasAllRequiredSettings = Boolean(settings.formName) && Boolean(settings.humanReadableName);
    const isTestingDisabled =
        isLoading || readOnly || (!settings.negativeTests && !settings.positiveTests) || !hasAllRequiredSettings;

    const onSaveButtonClick = useCallback(async () => {
        if (settings.sourceText.split('\n').length > 500) {
            const errorMessage = t('no-more-than-500-lines-error');
            toasterAdd &&
                toasterAdd({
                    title: errorMessage,
                    theme: 'danger',
                    name: GRAMMAR_PAGE_INTENT_TOASTER,
                    autoHiding: SHOW_TOASTER_DELAY,
                });
            throw errorMessage;
        } else if (!hasAllRequiredSettings) {
            const errorMessage = t('not-all-required-fields-error');
            toasterAdd &&
                toasterAdd({
                    title: errorMessage,
                    theme: 'danger',
                    name: GRAMMAR_PAGE_INTENT_TOASTER,
                    autoHiding: SHOW_TOASTER_DELAY,
                });
            throw errorMessage;
        }

        try {
            await onSaveHandler();
        } catch (e) {
            if (e instanceof ApiError && e.isDuplicateIntentIdError()) {
                toasterAdd &&
                    toasterAdd({
                        title: t('intent-with-specified-id-error'),
                        theme: 'danger',
                        name: GRAMMAR_PAGE_INTENT_TOASTER,
                        autoHiding: SHOW_TOASTER_DELAY,
                    });
            } else if (e instanceof ApiError && e.payload?.errorCode === 'GRAMMAR_SIZE_LIMIT') {
                toasterAdd &&
                    toasterAdd({
                        title: t('size-exceed-error'),
                        theme: 'danger',
                        name: GRAMMAR_PAGE_INTENT_TOASTER,
                        autoHiding: SHOW_TOASTER_DELAY,
                    });
            }

            throw e;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSaveHandler, settings.sourceText, settings.formName]);

    const onCodeChange = (code: string) => {
        onChange('sourceText')(code);
        setValidationError(null);
    };

    return (
        <>
            <header className={styles.grammarPageIntentSettings__header}>
                <div className={styles.grammarPageIntentSettings__headerTitle}>
                    <h2 className={styles.grammarPageIntentSettings__pageTitle}>{t('intent-settings')}</h2>
                    <Status status={intent.status} />
                </div>
                <Button onClick={onClose} size="l" view="flat">
                    <Icon data={Xmark} />
                </Button>
            </header>
            <section className={styles.grammarPageIntentSettings__content}>
                <div className={styles.grammarPageIntentSettings__column}>
                    <GrammarPageIntentSettingsField title={t('title-name')} hint={t('hint-name')} required>
                        <TextInput
                            value={settings.humanReadableName}
                            onUpdate={onChange('humanReadableName')}
                            size="xl"
                            readOnly={readOnly}
                        />
                    </GrammarPageIntentSettingsField>
                    <GrammarPageIntentSettingsField title="ID" required hint={t('hint-id')} error={errors.formName}>
                        <TextInput
                            value={settings.formName}
                            onUpdate={onChange('formName')}
                            size="xl"
                            readOnly={readOnly}
                        />
                    </GrammarPageIntentSettingsField>
                    <h4 className={styles.grammarPageIntentSettings__title}>
                        {t('title-grammar')}
                        <Hint
                            hint={
                                <>
                                    {wrapInText((s) => (
                                        <Link href={routes.documentation.aliceSkills.nlu} target="_blank">
                                            {s}
                                        </Link>
                                    ))(t('hint-grammar'))}
                                </>
                            }
                        />
                    </h4>
                    <GranetCodeEditor
                        code={settings.sourceText}
                        onChange={onCodeChange}
                        readOnly={readOnly || isLoading}
                        validationError={validationError || undefined}
                        onSave={onSaveButtonClick}
                        completionProvider={completionProvider}
                    />
                </div>
                <div className={styles.grammarPageIntentSettings__column}>
                    <GrammarPageIntentSettingsField title={t('title-positive-tests')} hint={t('hint-positive-tests')}>
                        <TextArea
                            className={styles.grammarPageIntentSettings__textArea}
                            value={settings.positiveTests}
                            onUpdate={readOnly ? () => undefined : onChange('positiveTests')}
                            rows={4}
                            readOnly={readOnly}
                        />
                    </GrammarPageIntentSettingsField>
                    <GrammarPageIntentSettingsField title={t('title-negative-tests')} hint={t('hint-negative-tests')}>
                        <TextArea
                            className={styles.grammarPageIntentSettings__textArea}
                            value={settings.negativeTests}
                            onUpdate={readOnly ? () => undefined : onChange('negativeTests')}
                            rows={4}
                            readOnly={readOnly}
                        />
                    </GrammarPageIntentSettingsField>
                    <GrammarPageIntentSettingsField title={t('title-result')}>
                        <div className={styles.grammarPageIntentSettings__testResults}>
                            {testResults ? <GrammarPageTestResult results={testResults} /> : null}
                        </div>
                    </GrammarPageIntentSettingsField>
                </div>
            </section>
            <footer className={styles.grammarPageIntentSettings__footer}>
                <div
                    className={clx(
                        styles.grammarPageIntentSettings__buttonGroup,
                        styles.grammarPageIntentSettings__buttonGroup_large,
                    )}
                >
                    <AliceAppActionButton
                        successLabel=""
                        action={onDeleteHandler}
                        size="l"
                        disabled={isLoading || readOnly}
                    >
                        {t('button-delete')}
                    </AliceAppActionButton>
                    <GrammarPageFlagsSettings intentId={intent.id} isActivationFlag={!!intent.isActivation} />
                </div>
                <div className={styles.grammarPageIntentSettings__buttonGroup}>
                    <AliceAppActionButton
                        successLabel={t('tooltip-tested')}
                        action={onTestHandler}
                        size="l"
                        disabled={isTestingDisabled}
                    >
                        {t('button-test')}
                    </AliceAppActionButton>
                    <AliceAppActionButton
                        action={onSaveButtonClick}
                        successLabel={t('changes-saved')}
                        size="l"
                        view="action"
                        disabled={isTesting || isDeleting || isSaving || readOnly || hasErrors}
                    >
                        {t('button-save')}
                    </AliceAppActionButton>
                </div>
            </footer>
        </>
    );
};
